import { Component, Input, forwardRef, ChangeDetectorRef } from '@angular/core';
import { FormArray, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import {
  CmdConnextService,
  DeleteAllDevicesInCertifiedPlantMutation,
  GenericDeviceValidator,
  PlantDeviceEnumType,
  PlantDeviceType,
  ProductModelService,
  System,
  UninstallAllDevicesInCertifiedPlantMutation,
  UpdateDeviceInCertifiedPlantMutation,
} from 'ngx-core-services';
import { BehaviorSubject } from 'rxjs';
import { TranslateModule } from '@ngx-translate/core';
import {
  ButtonToggleSelectModule,
  CardAccordionComponent,
  CustomerItemComponent,
  SimpleButtonComponent,
  SimpleInputComponent,
  TitleContentComponent,
  ToastFacade,
} from 'ngx-zcs-components';
import { MatChipsModule } from '@angular/material/chips';
import { CommonModule } from '@angular/common';
import { MatOptionModule } from '@angular/material/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatButtonModule } from '@angular/material/button';
import gql from 'graphql-tag';

import { CreateFirebaseBlockService } from '../create-firebase/create-firebase-block.service';
import { DeviceSerialValidatorPipe } from '../pipes/errors.pipe';
import { InverterValidators } from '../virtual-devices-section/virtual-devices-section.component';

export const switchSetupConnextInverter = (inverter: InverterForm) => () => {
  const currentValue = inverter.controls.setup.value;
  inverter.controls.setup.setValue(!currentValue);
};

export type ConnextDevicesForm = FormGroup<{
  productionSensor: FormGroup<{
    productionSensorK: FormControl<number | null>;
    id: FormControl<string | null>;
    registered: FormControl<boolean | null>;
  }>;
  inverterList: FormArray<
    FormGroup<{
      inverterSerial: FormControl<string | null>;
      inverterModbusId: FormControl<string | null>;
      setup: FormControl<boolean | null>;
      registered: FormControl<boolean | null>;
    }>
  >;
}>;

type InverterForm = FormGroup<{
  inverterSerial: FormControl<string | null>;
  inverterModbusId: FormControl<string | null>;
  setup: FormControl<boolean | null>;
  registered: FormControl<boolean | null>;
}>;

type Mode = 'inverters' | 'sensors';

const labels = (() => ({
  applyRegistration: 'Apply registration',
  active: 'Active',
  certifiedPlantsInverters: 'Certified plant\'s inverters',
  certifiedPlantsInvertersDescription: 'This list of inverters belong to the composition of your certified system. If you want to remove them, click on the "Restore" button.',
  connextsInverters: 'Connext\'s inverters',
  delete: 'Delete',
  deviceAlreadyOnACertifiedPlant: 'Device already on a certified plant',
  deviceNotExist: 'Device does not exist',
  error: 'Error',
  inverterListDescription: 'Enter the list of your devices specifying the serial numbers and the Modbus address. You can find the device serial number on the adhesive label. For example, for inverters, the label is located on the side.',
  inverterListPlaceholder: 'E.g. ZM2',
  inverterModbusDescription: 'Enter the Modbus address already assigned to the inverter',
  inverterModbusDescriptionInnerCard: 'Make sure to enter the Modbus (RS485) address configured on the inverter. If you have any doubts, contact the installer.',
  inverterModbusPlaceholder: 'E.g. 1',
  inverterRegistrationError: 'Error registering the inverter',
  inverters: 'Inverters',
  multiplicativeConstant: 'Multiplicative constant',
  productionSensor: 'Production Sensor',
  productionSensorDesc: 'The reading of this sensor will be used to calculate the power production',
  productionSensorDescWithConstMultiplicative: 'Set the multiplicative constant, it will be used in the calculation of the produced power',
  productionSensorPlaceholder: 'E.g. 3',
  registered: 'Registered',
  restore: 'Restore',
  restoreDevicesAndSensors: 'Restore devices and sensors',
  restoreDevicesAndSensorsDescription: 'This action will remove all added devices and sensors and restore the default configuration.',
  sensorRegistrationError: 'Error registering the sensor',
  sensors: 'Sensors',
  serialNotValid: 'Serial not valid',
  setup: 'Settings',
  uninstall: 'Uninstall',
  uninstalled: 'Uninstalled',
  uniqueSerial: 'The serial number must be unique',
  updatingSystemConfiguration: 'Updating system configuration',
  validationError: 'Validation error',
}))();

@Component({
  standalone: true,
  imports: [
    ButtonToggleSelectModule,
    CommonModule,
    DeviceSerialValidatorPipe,
    forwardRef(() => CardAccordionComponent),
    forwardRef(() => CustomerItemComponent),
    forwardRef(() => SimpleButtonComponent),
    forwardRef(() => SimpleInputComponent),
    forwardRef(() => TitleContentComponent),
    MatButtonModule,
    MatChipsModule,
    MatFormFieldModule,
    MatOptionModule,
    MatSelectModule,
    ReactiveFormsModule,
    TranslateModule,
  ],
  selector: 'customer-connext-devices-section',
  templateUrl: './connext-devices-section.component.html',
  styleUrls: ['./connext-devices-section.component.scss'],
})
export class ConnextDevicesSectionComponent {
  @Input() connextDevicesConfig!: ConnextDevicesForm;
  @Input() is3PhasePlant = false;
  @Input() connextSerial?: string | null;
  @Input() isVerifiedMode = true;
  @Input() applyChangeToCertifiedPlant?: string | null;
  @Input() certifiedPlantDevices: PlantDeviceType[] = [];
  @Input() certifiedPlantUninstalledDevices: PlantDeviceType[] = [];
  @Input() system: System | undefined;

  readonly _labels = labels;
  @Input() set labels(value: Partial<typeof this._labels>) {
    Object.assign(this._labels, value);
  }
  get labels(): typeof this._labels {
    return this._labels;
  }

  @Input() inverterValidators: InverterValidators = {
    syncValidators: [],
    asyncValidators: [],
  };

  readonly options: Array<keyof typeof labels> = ['active', 'uninstalled'];

  readonly mode$ = new BehaviorSubject<Mode>('inverters');

  readonly switchSetupConnextInverter = switchSetupConnextInverter;

  get connextInverterList() {
    return this.connextDevicesConfig.controls.inverterList;
  }

  get isConnextDeviceInInitialConfig() {
    return this.connextInverterList.length === 1;
  }

  get connextProductionSensor() {
    return this.connextDevicesConfig.controls.productionSensor;
  }

  get isConnextProductionSensorInvalid() {
    return this.is3PhasePlant
      ? this.connextProductionSensor.controls.productionSensorK.invalid
      : false;
  }

  constructor(
    private readonly changeDetectorRef: ChangeDetectorRef,
    private readonly cmdConnextService: CmdConnextService,
    private readonly deleteAllDevicesInCertifiedPlantMutation: DeleteAllDevicesInCertifiedPlantMutation,
    private readonly genericDeviceValidator: GenericDeviceValidator,
    private readonly toastFacade: ToastFacade,
    private readonly uninstallAllDevicesInCertifiedPlantMutation: UninstallAllDevicesInCertifiedPlantMutation,
    private readonly updateDeviceInCertifiedPlantMutation: UpdateDeviceInCertifiedPlantMutation,
    readonly createFirebaseBlockService: CreateFirebaseBlockService,
  ) {
    this.addConnextInverter = this.addConnextInverter.bind(this);
    this.registerConnextProductionSensor = this.registerConnextProductionSensor.bind(this);
  }

  async registerConnextProductionSensor() {
    const noAutoOwner = this.isVerifiedMode;
    const productionSensorK = this.connextProductionSensor.controls.productionSensorK.value ?? 1;

    if (!this.connextSerial) {
      return;
    }

    const uid = await this.genericDeviceValidator.getConnextUid(this.connextSerial);
    if (!uid) {
      return;
    }

    const connextSensorId = `XX${uid.toUpperCase()}X`;
    const cmd = `reg i s ${connextSensorId} ${this.is3PhasePlant ? 1 : 0} 1 ${(this.is3PhasePlant ? productionSensorK : 1) * 10}`;

    const response = await this.cmdConnextService.sendCommandStrategy(
      uid,
      cmd,
      this.labels.updatingSystemConfiguration,
      this.isVerifiedMode,
      false,
      noAutoOwner,
      this.system?.uid,
    );

    if (response?.error) {
      this.toastFacade.present(
        this.labels.sensorRegistrationError,
        this.labels.error,
        {
          severity: 'error',
        },
      );
      return;
    }

    this.connextProductionSensor.setValue({ productionSensorK, id: connextSensorId, registered: true });
    this.connextInverterList.clear();
  }

  registerConnextInverter(inverter: InverterForm) {
    return async () => {
      const noAutoOwner = this.isVerifiedMode;
      /**
       * Check if the inverter serial is already added to the list
       */
      const deviceIds = this.connextInverterList.value
        .filter((inv) => inv.registered)
        .map((inv) => inv.inverterSerial?.toLowerCase());

      if (deviceIds.includes(inverter.controls.inverterSerial.value?.toLowerCase())) {
        inverter.controls.inverterSerial.setErrors({ duplicate: true });
        return;
      }

      const serial = inverter.controls.inverterSerial.value?.trim();
      if (!serial) {
        inverter.controls.inverterSerial.setErrors({ required: true });
        return;
      }
      inverter.controls.inverterSerial.setValue(serial);

      if (!this.connextSerial) {
        return;
      }

      const uid = await this.genericDeviceValidator.getConnextUid(this.connextSerial);
      if (!uid) {
        return;
      }

      const inverterSerial = inverter.controls.inverterSerial.value as string;
      const inverterModbusId = inverter.controls.inverterModbusId.value;
      const currentProductModel = new ProductModelService(inverterSerial);
      const currentDevicePowerType = currentProductModel.type?.name === 'inverter' ? currentProductModel.type.phase : '';
      const isCurrentDeviceThreePhase = currentDevicePowerType === 'three-phase' ? 1 : 0;

      const cmd = `reg i s ${inverterSerial?.toUpperCase()} ${isCurrentDeviceThreePhase} ${inverterModbusId}`;

      const response = await this.cmdConnextService.sendCommandStrategy(
        uid,
        cmd,
        this.labels.updatingSystemConfiguration,
        this.isVerifiedMode,
        false,
        noAutoOwner,
        this.system?.uid,
      );

      if (response?.error) {
        this.toastFacade.present(
          this.labels.inverterRegistrationError,
          this.labels.error,
          {
            severity: 'error',
          },
        );
        return;
      }

      inverter.controls.registered.setValue(true);

      if (this.applyChangeToCertifiedPlant) {
        const query = gql`
        fragment certifiedplanttype on CertifiedPlantType {
          success
        }
        `;
        this.updateDeviceInCertifiedPlantMutation.execute(query, {
          plantId: this.applyChangeToCertifiedPlant,
          device: {
            sn: serial,
            installationTime: (new Date()).getTime(),
            type: PlantDeviceEnumType.INVERTER,
          }
        });
      }

      this.addConnextInverter();
    };
  }

  async removeAllCurrentConnextDevices(action: 'uninstall' | 'delete') {
    const noAutoOwner = this.isVerifiedMode;

    this.connextInverterList.clear();
    this.addConnextInverter();
    this.connextProductionSensor.reset();

    if (!this.connextSerial) {
      return;
    }

    const uid = await this.genericDeviceValidator.getConnextUid(this.connextSerial);
    if (!uid) {
      return;
    }

    const response = await this.cmdConnextService.sendCommandStrategy(
      uid,
      'reg i UA',
      this.labels.updatingSystemConfiguration,
      this.isVerifiedMode,
      false,
      noAutoOwner,
      this.system?.uid,
    );
    if (response?.error) {
      return;
    }

    if (this.applyChangeToCertifiedPlant) {
      const query = gql`
      fragment certifiedplanttype on CertifiedPlantType {
        success
      }
      `;

      if (action === 'uninstall') {
        this.uninstallAllDevicesInCertifiedPlantMutation.execute(query, { plantId: this.applyChangeToCertifiedPlant });
      } else if (action === 'delete') {
        this.deleteAllDevicesInCertifiedPlantMutation.execute(query, { plantId: this.applyChangeToCertifiedPlant });
      }
    }
  }

  addConnextInverter() {
    this.connextInverterList.push(new FormGroup({
      inverterSerial: new FormControl('', this.inverterValidators.syncValidators, this.inverterValidators.asyncValidators),
      inverterModbusId: new FormControl('', [
        Validators.required,
        Validators.min(1),
        Validators.max(255),
        Validators.pattern('^[0-9]*$'),
      ]),
      setup: new FormControl(false),
      registered: new FormControl(false),
    }));
    this.changeDetectorRef.detectChanges();
  }
}
